import {useLocationState} from '@raison/navigation';
import arrowRightIcon from '@raison/static/icons/arrow-right-s-line.svg';
import customerServiceIcon from '@raison/static/icons/customer-service-2-line.svg';
import lockIcon from '@raison/static/icons/lock-line.svg';
import mailIcon from '@raison/static/icons/mail-line.svg';
import mapPinIcon from '@raison/static/icons/map-pin-2-line.svg';
import phoneIcon from '@raison/static/icons/phone-line.svg';
import {capitalize} from '@raison/toolbox';
import {
    cn,
    InlineSvg,
    maskEmail,
    RootLayout,
    RsnDialog,
    RsnMessage,
    RsnPane,
    RsnSkeleton,
    RsnTextButton,
} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import {Atom, useAtomValue} from 'jotai';
import {AtomWithQueryResult} from 'jotai-tanstack-query';
import React from 'react';
import {Link, useNavigate} from 'react-router';
import {AddressStatus, UserStatus} from '~/entities/user';
import {Address} from '../../model/Address';
import {AddressDialog} from './AddressDialog';
import {CloseAccountModal} from './CloseAccountModal/CloseAccountModal';

interface ProfileData {
    email: string;
    phoneNumber: string;
    givenName?: string | null;
    familyName?: string | null;
    address?: Address;
    addressStatus?: AddressStatus;
    onboardingStatus: UserStatus;
}

export interface ProfilePageProps {
    userProfileIdentity: Atom<AtomWithQueryResult<ProfileData>>;
}

export function ProfilePage({userProfileIdentity}: ProfilePageProps) {
    const navigate = useNavigate();

    const {data: {givenName, familyName, phoneNumber, email, addressStatus, address} = {}, isSuccess} =
        useAtomValue(userProfileIdentity);

    const [dialog, setDialog] = useLocationState('profile-page-dialog', Schema.Literal('account-closure', 'address'));
    const closeDialog = () => navigate(-1);

    const [addressStatusText, addressStatusClasses] = addressStatus
        ? (() => {
              switch (addressStatus) {
                  case 'success': {
                      return [null, ''];
                  }
                  case 'pending': {
                      return ['Under review', 'text-warning-30'];
                  }
                  default: {
                      return ['Verification required', 'text-danger-30'];
                  }
              }
          })()
        : [null, ''];

    const t = {
        addressLabel: 'Residential address',
    };

    return (
        <RootLayout title='My profile'>
            <div className={'flex flex-1 flex-col'}>
                <div className={'flex-1'}>
                    <div className={'fv-heading-l px-4 pb-4 pt-6 text-neutral-100'}>
                        {isSuccess ? (
                            `${capitalize(givenName ?? '')} ${capitalize(familyName ?? '')}`
                        ) : (
                            <RsnSkeleton width={200} height={28} />
                        )}
                    </div>
                    <RsnPane>
                        <RsnPane.IconFrame>
                            <RsnPane.SVGIcon src={phoneIcon} />
                        </RsnPane.IconFrame>
                        <RsnPane.TextBlock>
                            <RsnPane.PrimaryText
                                meta={isSuccess ? phoneNumber : <RsnSkeleton width={100} height={20} />}
                            >
                                Phone
                            </RsnPane.PrimaryText>
                        </RsnPane.TextBlock>
                    </RsnPane>
                    <Delimiter />
                    <RsnPane>
                        <RsnPane.IconFrame>
                            <RsnPane.SVGIcon src={mailIcon} />
                        </RsnPane.IconFrame>
                        <RsnPane.TextBlock>
                            <RsnPane.PrimaryText
                                meta={isSuccess ? maskEmail(email ?? '') : <RsnSkeleton width={100} height={20} />}
                            >
                                Email
                            </RsnPane.PrimaryText>
                        </RsnPane.TextBlock>
                    </RsnPane>
                    <Delimiter />
                    {isSuccess ? (
                        <RsnPane as={'button'} className={'cursor-pointer'} onClick={() => setDialog('address')}>
                            <RsnPane.IconFrame>
                                <RsnPane.SVGIcon src={mapPinIcon} />
                            </RsnPane.IconFrame>
                            <RsnPane.TextBlock>
                                <RsnPane.PrimaryText>{t.addressLabel}</RsnPane.PrimaryText>
                                {addressStatusText && (
                                    <RsnPane.SecondaryText>
                                        <span className={addressStatusClasses}>{addressStatusText}</span>
                                    </RsnPane.SecondaryText>
                                )}
                            </RsnPane.TextBlock>
                            <InlineSvg src={arrowRightIcon} className={'size-5 text-neutral-90'} />
                        </RsnPane>
                    ) : (
                        <RsnPane>
                            <RsnPane.IconFrame>
                                <RsnPane.SVGIcon src={mapPinIcon} />
                            </RsnPane.IconFrame>
                            <RsnPane.TextBlock>
                                <RsnPane.PrimaryText>{t.addressLabel}</RsnPane.PrimaryText>
                            </RsnPane.TextBlock>
                        </RsnPane>
                    )}
                    <Delimiter />
                    <RsnPane as={Link} to={'/change-password'} className={'cursor-pointer'}>
                        <RsnPane.IconFrame>
                            <RsnPane.SVGIcon src={lockIcon} />
                        </RsnPane.IconFrame>
                        <RsnPane.TextBlock>
                            <RsnPane.PrimaryText>Password</RsnPane.PrimaryText>
                        </RsnPane.TextBlock>
                        <RsnTextButton.Inline size={'l'}>Change</RsnTextButton.Inline>
                    </RsnPane>
                </div>
                <div className={'flex flex-col gap-4 p-4'}>
                    <RsnMessage.FootnoteV2 iconSrc={customerServiceIcon} centered>
                        Upon any changes or inaccuracies in your personal data, please feel free to contact our{' '}
                        <Link to={'/support/form'} className={'text-primary-30'}>
                            support service
                        </Link>
                        .
                    </RsnMessage.FootnoteV2>
                    <button
                        onClick={() => setDialog('account-closure')}
                        className={cn([
                            'fv-text-l-regular flex h-12 w-full items-center justify-center px-4 text-danger-30',
                            'transition-colors duration-300 active:text-danger-40 active:duration-0',
                        ])}
                    >
                        Close account
                    </button>
                </div>
            </div>

            <RsnDialog.Controller onClose={closeDialog}>
                {(() =>
                    (dialog === 'account-closure' && <CloseAccountModal navigationReplace />) ||
                    (dialog === 'address' && (
                        <AddressDialog {...address} addressStatus={addressStatus} navigationReplace />
                    )) ||
                    null)()}
            </RsnDialog.Controller>
        </RootLayout>
    );
}

function Delimiter() {
    return <hr className={'mx-4 h-px border-none bg-neutral-30'} />;
}
